import { useState } from "react";
import { Modal } from "@/components/base/Modal";
import AddressFields, { type AddressValues } from "@/components/feature/AddressFields";
import { POST_PICK_HEADER_EDIT_WARNING, type SalesOrderEditFormValues } from "@/lib/sales-order-edit";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export type SalesOrderEditValues = SalesOrderEditFormValues;

export interface EditSalesOrderModalProps {
  open: boolean;
  onClose: () => void;
  saving: boolean;
  error: string;
  postFulfilment: boolean;
  locked: boolean;
  lockReason: string;
  linesLocked: boolean;
  initial: SalesOrderEditValues;
  onSave: (values: SalesOrderEditValues) => void;
}

export function EditSalesOrderModal({
  open,
  onClose,
  saving,
  error,
  postFulfilment,
  locked,
  lockReason,
  linesLocked,
  initial,
  onSave,
}: EditSalesOrderModalProps) {
  const [shipping, setShipping] = useState<AddressValues>(initial.shipping);
  const [billing, setBilling] = useState<AddressValues>(initial.billing);
  const [priority, setPriority] = useState(initial.priority || "normal");
  const [customerReference, setCustomerReference] = useState(initial.customerReference);
  const [notes, setNotes] = useState(initial.notes);

  const submit = () => {
    if (locked) return;
    onSave({ shipping, billing, priority, customerReference, notes });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit order"
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || locked}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div role="alert" className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
        {locked && (
          <div role="status" className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
            {lockReason}
          </div>
        )}
        {postFulfilment && !locked && (
          <div role="status" className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800">
            {POST_PICK_HEADER_EDIT_WARNING}
          </div>
        )}
        {linesLocked && (
          <p data-testid="line-edits-locked" className="text-xs text-slate-500">
            Line quantity, unit price, and add/remove are locked for this order.
          </p>
        )}
        <fieldset disabled={locked} className="space-y-4 disabled:opacity-60">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="so-edit-priority">
                Priority
              </label>
              <select id="so-edit-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="so-edit-reference">
                Customer reference
              </label>
              <input
                id="so-edit-reference"
                value={customerReference}
                onChange={(e) => setCustomerReference(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="so-edit-notes">
              Notes
            </label>
            <textarea
              id="so-edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={inputCls}
            />
          </div>
          <AddressFields idPrefix="so-edit-shipping" label="Shipping address" value={shipping} onChange={setShipping} />
          <AddressFields idPrefix="so-edit-billing" label="Billing address" value={billing} onChange={setBilling} />
        </fieldset>
      </div>
    </Modal>
  );
}
